import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State variables
  const [job, setJob] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessLogo, setBusinessLogo] = useState(null);
  const [hasApplied, setHasApplied] = useState(false);
  const [similarJobs, setSimilarJobs] = useState([]);
  
  // Fetch job and business data
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        console.log(`Fetching job details for job ID: ${jobId}`);
        
        // Get job data
        const jobDoc = await getDoc(doc(firestore, 'jobs', jobId));
        
        if (!jobDoc.exists()) {
          console.log('Job not found');
          setLoading(false);
          return;
        }
        
        const jobData = jobDoc.data();
        console.log('Job data:', jobData);
        
        // Get business data
        if (jobData.businessId) {
          const businessDoc = await getDoc(doc(firestore, 'businesses', jobData.businessId));
          if (businessDoc.exists()) {
            const businessData = businessDoc.data();
            setBusiness(businessData);
            
            // Get business logo
            if (businessData.businessImage && businessData.businessImage.path) {
              try {
                const logoRef = ref(storage, businessData.businessImage.path);
                const logoUrl = await getDownloadURL(logoRef);
                setBusinessLogo(logoUrl);
              } catch (error) {
                console.log('Error getting business logo:', error);
              }
            }
          }
        }
        
        // Format job data
        const postedDate = jobData.createdAt?.toDate?.() || (jobData.createdAt?.__time__ ? new Date(jobData.createdAt.__time__) : new Date());
        const diffTime = new Date() - postedDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // Enhanced salary formatting to handle rate ranges
        let formattedSalary = 'Salary not specified';
        if (jobData.hourlyRate) {
          const hourlyRate = typeof jobData.hourlyRate === 'number' 
              ? jobData.hourlyRate 
              : parseFloat(jobData.hourlyRate);
          
          if (!isNaN(hourlyRate)) {
            if (hourlyRate > 1000) {
              formattedSalary = `$${(hourlyRate/1000).toFixed(0)}k/year`;
            } else {
              formattedSalary = `$${hourlyRate.toFixed(0)}/hr`;
            }
          }
        } else if (jobData.salary) {
          formattedSalary = jobData.salary;
        }
        
        // Check for salary range in description (e.g., "$18-19 per hour")
        if (jobData.description) {
          const salaryRangeMatch = jobData.description.match(/\$(\d+)-(\d+)\s*per\s*hour/i);
          if (salaryRangeMatch) {
            const minRate = parseInt(salaryRangeMatch[1]);
            const maxRate = parseInt(salaryRangeMatch[2]);
            formattedSalary = `$${minRate}-${maxRate}/hr`;
          }
        }
        
        setJob({
          id: jobId,
          title: jobData.title || 'Untitled Position',
          description: jobData.description || 'No description available',
          salary: formattedSalary,
          location: jobData.address?.city || 'Location not specified',
          address: jobData.address?.street 
            ? `${jobData.address.street}, ${jobData.address.city || ''}, ${jobData.address.state || ''} ${jobData.address.zipcode || ''}`
            : jobData.address?.city 
              ? `${jobData.address.city}, ${jobData.address.state || ''} ${jobData.address.zipcode || ''}`.trim()
              : 'Address not specified',
          jobType: jobData.jobType || 'Not specified',
          experienceLevel: jobData.experienceLevel || 'Not specified',
          qualifications: jobData.qualifications || 'Not specified',
          responsibilities: jobData.responsibilities || 'Not specified',
          benefits: jobData.benefits || 'Not specified',
          postedDays: diffDays,
          isActive: jobData.isActive !== false, // default to true if not specified
          immediateStart: jobData.isImmediateStart,
          workSchedule: jobData.workSchedule || 'Not specified',
          applicationUrl: jobData.applicationUrl || null,
          businessId: jobData.businessId,
          // New optimized fields
          requiredSkills: jobData.requiredSkills || [],
          extraCompensations: jobData.extraCompensations || [],
          startTime: jobData.startTime || null,
          applicationCount: jobData.applicationCount || 0,
          hourlyRate: jobData.hourlyRate
        });
        
        // Check if user has applied
        if (currentUser) {
          const applicationsQuery = query(
            collection(firestore, 'jobApplications'),
            where('userId', '==', currentUser.uid),
            where('jobId', '==', jobId)
          );
          
          const applicationsSnapshot = await getDocs(applicationsQuery);
          setHasApplied(!applicationsSnapshot.empty);
        }
        
        // Fetch similar jobs based on job type or location
        if (jobData.jobType || jobData.address?.city) {
          let similarJobsQuery;
          
          if (jobData.jobType) {
            similarJobsQuery = query(
              collection(firestore, 'jobs'),
              where('jobType', '==', jobData.jobType),
              where('isActive', '==', true),
              limit(4)
            );
          } else if (jobData.address?.city) {
            similarJobsQuery = query(
              collection(firestore, 'jobs'),
              where('address.city', '==', jobData.address.city),
              where('isActive', '==', true),
              limit(4)
            );
          }
          
          if (similarJobsQuery) {
            const similarJobsSnapshot = await getDocs(similarJobsQuery);
            const similarJobsData = similarJobsSnapshot.docs
              .filter(doc => doc.id !== jobId) // Exclude current job
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
                postedAt: doc.data().createdAt?.toDate?.() || new Date()
              }));
            
            setSimilarJobs(similarJobsData);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching job details:', error);
        setLoading(false);
      }
    };
    
    fetchJobDetails();
  }, [jobId, currentUser]);
  
  // Handle apply for job
  const handleApply = async () => {
    if (!currentUser) {
      // Redirect to login page with return URL
      navigate(`/login?redirect=/jobs/${jobId}`);
      return;
    }
    
    try {
      // Handle external applications (open URL)
      if (job.applicationUrl) {
        console.log('External application:', job.applicationUrl);
        window.open(job.applicationUrl, '_blank');
        return;
      }
      
      // Handle internal applications (save to database)
      console.log(`Applying for job ${jobId} using ApplicationService...`);
      console.log(`Job details:`, { jobId, businessId: job.businessId, title: job.title });
      
      // Validate we have both jobId and businessId
      if (!job.businessId) {
        console.error('❌ Job is missing businessId:', job);
        alert('Error: Job is missing business information. Please try again.');
        return;
      }
      
      // Use ApplicationService to handle application
      const ApplicationService = (await import('../../services/ApplicationService')).ApplicationService;
      const result = await ApplicationService.submitApplication({
        userId: currentUser.uid,
        jobId: jobId,
        businessId: job.businessId
      });
      
      if (result.success) {
        setHasApplied(true);
        // alert(result.message || 'Application submitted successfully!'); // Disabled notification
      } else {
        alert(result.message || 'Failed to submit application');
      }
      
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }
  
  if (!job) {
    return (
      <div className="flex flex-col items-start justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white rounded-xl shadow-sm p-10 max-w-2xl w-full">
          <div className="bg-red-100 p-4 rounded-full inline-block mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3 text-left">Job Not Found</h1>
          <p className="text-gray-600 mb-6 text-left">
            The job you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/dashboard/jobs')}
            className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors"
          >
            Browse All Jobs
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header with back button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-700 hover:text-[#FF8C00] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <span>Back to Jobs</span>
          </button>
        </div>
        
        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Job details - 2/3 width */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              {/* Job header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-800 mb-2 text-left">{job.title}</h1>
                  <div className="flex flex-wrap items-center text-gray-600 text-sm gap-3 mb-4 text-left">
                    {business && (
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span onClick={() => navigate(`/dashboard/businesses/${job.businessId}`)} className="hover:text-[#FF8C00] cursor-pointer transition-colors">
                          {business.name}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{job.jobType}</span>
                    </div>
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                      </svg>
                      <span className="font-semibold text-[#FF8C00]">{job.salary}</span>
                    </div>
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Posted {job.postedDays} {job.postedDays === 1 ? 'day' : 'days'} ago</span>
                    </div>
                  </div>
                  
                  {/* Application count and start time info */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4 text-left">
                    {job.applicationCount > 0 && (
                      <div className="flex items-center bg-blue-50 px-3 py-1.5 rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-blue-700 font-medium">{job.applicationCount} applications</span>
                      </div>
                    )}
                    {job.startTime && job.startTime !== 'Flexible start date' && (
                      <div className="flex items-center bg-green-50 px-3 py-1.5 rounded-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-green-700 font-medium">Start: {job.startTime}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 flex flex-col items-end gap-2">
                  {job.isActive ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Inactive
                    </span>
                  )}
                  {job.immediateStart && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Immediate Start
                    </span>
                  )}
                </div>
              </div>
              
              {/* Apply button */}
              <div className="mb-8">
                <button
                  onClick={handleApply}
                  disabled={hasApplied || !job.isActive}
                  className={`w-full py-3 rounded-lg text-white font-medium transition-colors ${
                    hasApplied
                      ? 'bg-gray-400 cursor-not-allowed'
                      : job.isActive
                        ? 'bg-[#FF8C00] hover:bg-[#FF8C00]/90'
                        : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {hasApplied ? 'Application Submitted' : job.isActive ? 'Apply Now' : 'Job No Longer Available'}
                </button>
                {hasApplied && (
                  <p className="text-sm mt-2 text-gray-600 text-left">
                    You've already applied for this job. Check your email for updates.
                  </p>
                )}
              </div>
              
              {/* Job description */}
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-3 text-left">Job Description</h2>
                <div className="prose prose-sm max-w-none text-gray-700 text-left">
                  <p className="whitespace-pre-line text-left">{job.description}</p>
                </div>
              </div>
              
              {/* Required Skills */}
              {job.requiredSkills && job.requiredSkills.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-3 text-left">Required Skills</h2>
                  <div className="flex flex-wrap gap-2">
                    {job.requiredSkills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Responsibilities */}
              {job.responsibilities && job.responsibilities !== 'Not specified' && (
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-3 text-left">Responsibilities</h2>
                  <div className="prose prose-sm max-w-none text-gray-700 text-left">
                    <p className="whitespace-pre-line text-left">{job.responsibilities}</p>
                  </div>
                </div>
              )}
              
              {/* Qualifications */}
              {job.qualifications && job.qualifications !== 'Not specified' && (
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-3 text-left">Qualifications</h2>
                  <div className="prose prose-sm max-w-none text-gray-700 text-left">
                    <p className="whitespace-pre-line text-left">{job.qualifications}</p>
                  </div>
                </div>
              )}
              
              {/* Extra Compensations & Benefits */}
              {job.extraCompensations && job.extraCompensations.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-3 text-left">Benefits & Perks</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {job.extraCompensations.map((benefit, index) => (
                      <div
                        key={index}
                        className="flex items-center px-4 py-3 bg-green-50 rounded-lg border border-green-200"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-green-800 font-medium text-left">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Legacy Benefits (fallback) */}
              {job.benefits && job.benefits !== 'Not specified' && (!job.extraCompensations || job.extraCompensations.length === 0) && (
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-3 text-left">Benefits</h2>
                  <div className="prose prose-sm max-w-none text-gray-700 text-left">
                    <p className="whitespace-pre-line text-left">{job.benefits}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Sidebar - 1/3 width */}
          <div className="lg:col-span-1">
            {/* Company information */}
            {business && (
              <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4 text-left">About the Company</h2>
                
                <div className="flex items-center mb-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                    {businessLogo ? (
                      <img src={businessLogo} alt={business.name} className="w-full h-full object-cover" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900 text-left">{business.name}</h3>
                    <p className="text-sm text-gray-600 text-left">{business.type || 'Business'}</p>
                  </div>
                </div>
                
                <div className="text-sm text-gray-700 mb-4 text-left">
                  {business.about || business.description || 'No description available'}
                </div>
                
                {/* Company details */}
                <div className="space-y-2 text-sm text-gray-700 text-left">
                  {/* Location */}
                  {business.address && (
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-left">
                        {(() => {
                          const addressParts = [];
                          if (business.address.street && business.address.street.trim()) {
                            addressParts.push(business.address.street.trim());
                          }
                          if (business.address.city && business.address.city.trim()) {
                            addressParts.push(business.address.city.trim());
                          }
                          if (business.address.state && business.address.state.trim()) {
                            addressParts.push(business.address.state.trim());
                          }
                          if (business.address.zipcode && business.address.zipcode.trim() && business.address.zipcode !== '0') {
                            addressParts.push(business.address.zipcode.trim());
                          }
                          return addressParts.join(', ') || 'Address not available';
                        })()}
                      </span>
                    </div>
                  )}
                  
                  {/* Website */}
                  {business.website && business.website.trim() && business.website !== '0' && (
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] hover:underline text-left">
                        {business.website.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    </div>
                  )}
                  
                  {/* Phone Number */}
                  {business.phoneNumber && business.phoneNumber.trim() && business.phoneNumber !== '0' && (
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="text-left">{business.phoneNumber}</span>
                    </div>
                  )}
                  
                  {/* Founded */}
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-left">
                      Founded in {(() => {
                        const year = business.yearFounded;
                        if (year && 
                            year !== 0 && 
                            year !== '0' && 
                            String(year).trim() !== '0' &&
                            parseInt(year) > 0 &&
                            !isNaN(year)) {
                          return year;
                        }
                        return 'unknown';
                      })()}
                    </span>
                  </div>
                  
                  {/* Employee count - comprehensive filtering */}
                  {(() => {
                    const empScale = business.employeeScale;
                    // Note: "51-200" is a valid employee range, so we need to handle ranges differently
                    const isValidEmployeeCount = empScale && 
                      empScale !== 0 && 
                      empScale !== '0' && 
                      String(empScale).trim() !== '0' &&
                      String(empScale).trim() !== '' &&
                      empScale !== 'Not specified' && 
                      empScale !== 'Not specified employees' &&
                      String(empScale).trim().length > 0;
                    
                    return isValidEmployeeCount ? (
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 914 0zM7 10a2 2 0 11-4 0 2 2 0 914 0z" />
                        </svg>
                        <span className="text-left">{empScale} employees</span>
                      </div>
                    ) : null;
                  })()}
                </div>
                
                <div className="mt-5">
                  <button
                    onClick={() => navigate(`/dashboard/businesses/${job.businessId}`)}
                    className="w-full py-2.5 border border-[#FF8C00] text-[#FF8C00] rounded-lg hover:bg-[#FF8C00]/10 transition-colors text-sm font-medium"
                  >
                    View Company Profile
                  </button>
                </div>
              </div>
            )}
            
            {/* Job details box */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 text-left">Job Details</h2>
              
              <div className="space-y-4">
                {/* Employment type */}
                <div className="flex">
                  <div className="w-8 flex-shrink-0 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 text-left">Employment Type</h3>
                    <p className="text-sm text-gray-700 text-left">{job.jobType}</p>
                  </div>
                </div>
                
                {/* Salary */}
                <div className="flex">
                  <div className="w-8 flex-shrink-0 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 text-left">Salary</h3>
                    <p className="text-sm text-gray-700 font-semibold text-[#FF8C00] text-left">{job.salary}</p>
                  </div>
                </div>
                
                {/* Work schedule */}
                <div className="flex">
                  <div className="w-8 flex-shrink-0 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 text-left">Schedule</h3>
                    <p className="text-sm text-gray-700 text-left">{job.workSchedule}</p>
                  </div>
                </div>

                {/* Start Time */}
                {job.startTime && (
                  <div className="flex">
                    <div className="w-8 flex-shrink-0 text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 text-left">Start Date</h3>
                      <p className="text-sm text-gray-700 text-left">{job.startTime}</p>
                    </div>
                  </div>
                )}

                {/* Experience level */}
                <div className="flex">
                  <div className="w-8 flex-shrink-0 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 text-left">Experience</h3>
                    <p className="text-sm text-gray-700 text-left">{job.experienceLevel}</p>
                  </div>
                </div>
                
                {/* Location */}
                <div className="flex">
                  <div className="w-8 flex-shrink-0 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 text-left">Location</h3>
                    <p className="text-sm text-gray-700 text-left">{job.address}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Similar jobs */}
            {similarJobs.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4 text-left">Similar Jobs</h2>
                <div className="space-y-4">
                  {similarJobs.map(similarJob => (
                    <div key={similarJob.id} className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0">
                      <a href={`/dashboard/jobs/${similarJob.id}`} target="_blank" rel="noopener noreferrer" className="block hover:bg-gray-50 p-2 -mx-2 rounded-lg transition-colors">
                        <h3 className="text-base font-medium text-gray-900 hover:text-[#FF8C00] truncate text-left">{similarJob.title}</h3>
                        <div className="flex flex-wrap items-center text-sm text-gray-600 mt-1 gap-x-3 text-left">
                          <span>{similarJob.address?.city || 'Location not specified'}</span>
                          <span>•</span>
                          <span>{similarJob.jobType || 'Not specified'}</span>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailPage; 